import { useRef } from "react";
import { useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import "./previewSales.css";
import OnDemandPDFExport from "../exports/OnDemandPDFExport.jsx";
import {
  transactionSummary,
  transactionLines,
  transactionDetails,
  recordTitles,
  purchaseAmount,
} from "../exports/SalesPDFExport.jsx";

// The Company Account record preview is read-only. Keep all permitted actions and
// the existing PDF/print data sources; only the item table presentation changes.
export function TransactionRecordPreview({
  record, kind = "sale", onClose, onEdit, onStatus, onResume, onPayment,
  busy, premium = false,
}) {
  const contentRef = useRef(null);
  const company = useSelector(state => state.clientFunction?.companiesDB?.clientData);
  const summary = transactionSummary(record || {}, kind);
  const print = useReactToPrint({ contentRef, documentTitle: String(summary.id) });

  if (!record) {
    return <div className="wrapPreviewSales"><p>Select a saved record to preview.</p><button onClick={onClose}>Close</button></div>;
  }

  const canEdit = kind === "invoice"
    ? summary.status === "DRAFT" && Number(record.payment?.paidAmount || 0) === 0
    : kind === "expense"
      ? summary.status === "RECORDED" && Number(record.payment?.paidAmount || 0) === 0
      : kind === "production" ? summary.status === "DRAFT" : true;

  const lines = transactionLines(record, kind);
  const transitions = {
    quotation: { draft: ["sent", "accepted", "rejected", "cancelled"], sent: ["accepted", "rejected", "cancelled"] },
    discount: { ACTIVE: ["INACTIVE"], INACTIVE: ["ACTIVE"] },
    subscription: { active: ["paused", "cancelled"], paused: ["active", "cancelled"] },
    draft: { draft: ["archived"], archived: ["draft"] },
    delivery: { PENDING: ["DISPATCHED", "CANCELLED"], DISPATCHED: ["DELIVERED"] },
    invoice: { DRAFT: ["ISSUED", "CANCELLED"], ISSUED: ["CANCELLED"] },
    production: { DRAFT: ["IN_PROGRESS", "CANCELLED"], IN_PROGRESS: ["COMPLETE", "CANCELLED"] },
    expense: { RECORDED: ["VOID"] },
  };
  const history = record.status?.history || record.statusHistory || [];
  // Show four essential item columns, plus recorded stock/batch/detail columns.
  // Nothing with an actual recorded value is hidden, and every row keeps exactly
  // the same number of cells as the table header.
  const showStock = lines.some(line => line.before != null || line.after != null || line.destinationAfter != null);
  const showDetail = lines.some(line => line.delta != null || Boolean(line.details));
  const showBatch = lines.some(line => Boolean(line.batch));
  const columnCount = 4 + Number(showStock) + Number(showDetail) + Number(showBatch);
  const managementPreviewKinds = ['pricegroup','unit','brand','category','tax','variation','service','delivery','transfer','opening','reconciliation','adjustment','execution'];
  const documentPreview = managementPreviewKinds.includes(kind);
  const displayItems = lines.length > 0 || !(['unit','brand','category','tax','variation','service','expense'].includes(kind));

  return <div className={`wrapPreviewSales ${premium ? "tx-premium-preview" : ""} ${documentPreview ? "p2-document-preview" : ""} ${["sale","expense"].includes(kind) ? (kind === "sale" ? "p2-sale-preview" : "p2-expense-preview") : ""}`} role="dialog" aria-label={`${recordTitles[kind]} ${summary.id}`}>
    <div className="fx-ac space1" style={{ padding: "16px", flexWrap: "wrap" }}>
      <button className="controlButtons" onClick={print}>Print</button>
      <OnDemandPDFExport currentRows={[record]} kind={kind} label="Export PDF" companyName={company?.businessProfile?.basicInfo?.businessName} />
      <button className="controlButtons" onClick={onClose}>Close</button>
      {onEdit && canEdit && <button className="controlButtons" disabled={busy} onClick={() => onEdit(record)}>Edit</button>}
      {onResume && summary.status === "draft" && <button className="controlButtons" onClick={() => onResume(record)}>Resume Draft</button>}
      {onPayment && summary.due > 0 && (kind === "invoice" ? summary.status === "ISSUED" : summary.status === "RECORDED") &&
        <button className="controlButtons" disabled={busy} onClick={() => onPayment(record)}>Record Payment</button>}
      {onStatus && (transitions[kind]?.[summary.status] || []).map(status =>
        <button className="controlButtons" key={status} disabled={busy} onClick={() => onStatus?.(record, status)}>
          {status === "COMPLETE" ? "Complete Production" : `Mark ${status}`}
        </button>)}
    </div>

    <div className="contPreviewSales fx-cl space3" ref={contentRef}>
      <header>
        <h2>{recordTitles[kind]}</h2>
        <h3>{company?.businessProfile?.basicInfo?.businessName || ""}</h3>
        <strong>{String(summary.id)}</strong>
      </header>
      {(["sale", "expense"].includes(kind) || documentPreview) ? <section className="p2-sale-details" aria-label="Record information">
        <h3>{kind === "sale" ? "Transaction information" : kind === "expense" ? "Expense information" : "Record information"}</h3>
        <dl>{transactionDetails(record, kind).map(([key, value]) =>
          <div key={key}><dt>{key}</dt><dd>{value}</dd></div>)}</dl>
      </section> : <div className="PreviewSalesDetails g g2 space2">
        {transactionDetails(record, kind).map(([key, value]) =>
          <div key={key}><strong>{key}</strong><p>{value}</p></div>)}
      </div>}

      {displayItems && <section className="tx-preview-items" aria-label="Items in this record">
        <div className="tx-preview-items-heading">
          <div>
            <h3>Items in this record</h3>
            <p>Recorded products, quantities and item details</p>
          </div>
          <div className="tx-preview-items-count">
            <strong>{lines.length}</strong> {lines.length === 1 ? "line item" : "line items"}
            <span aria-hidden="true">·</span>
            <strong>{summary.quantity}</strong> units
          </div>
        </div>
        {lines.length ? <>
          <p className={`tx-preview-scroll-hint ${columnCount >= 6 ? "tx-preview-scroll-hint--wide" : ""}`}>Scroll across the item table to see additional details.</p>
          <div className="tx-preview-items-scroll" role="region" aria-label="Recorded item details" tabIndex={0}>
            <table className={`tx-preview-items-table tx-preview-items-table--cols-${columnCount}`} aria-label="Recorded line items">
              <thead>
                <tr>
                  <th scope="col">Item / SKU</th>
                  <th scope="col" className="tx-preview-numeric">Units</th>
                  <th scope="col" className="tx-preview-numeric">Unit price / cost</th>
                  <th scope="col" className="tx-preview-numeric">Value</th>
                  {showStock && <th scope="col">Before → After</th>}
                  {showDetail && <th scope="col">Change / Detail</th>}
                  {showBatch && <th scope="col">Batch</th>}
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => <tr key={index}>
                  <td className="tx-preview-product-cell">
                    <span className="tx-preview-product-number">{String(index + 1).padStart(2, "0")}</span>
                    <div className="tx-preview-product-info">
                      <strong>{line.name}</strong>
                      <span className="tx-preview-sku">SKU: {line.sku || "Not recorded"}</span>
                    </div>
                  </td>
                  <td className="tx-preview-numeric tx-preview-units">{line.quantity ?? "Not recorded"}</td>
                  <td className="tx-preview-numeric">{purchaseAmount(line.price, summary.currency)}</td>
                  <td className="tx-preview-numeric tx-preview-value">{purchaseAmount(line.total, summary.currency)}</td>
                  {showStock && <td className="tx-preview-stock">
                    {line.before == null && line.after == null ? <span className="tx-preview-muted">Not recorded</span> : <>
                      <span>{line.before ?? "Not recorded"}</span><span className="tx-preview-arrow" aria-hidden="true">→</span><strong>{line.after ?? "Not recorded"}</strong>
                    </>}
                    {line.destinationAfter != null && <span className="tx-preview-destination">Destination: {line.destinationAfter}</span>}
                  </td>}
                  {showDetail && <td className="tx-preview-detail">{line.delta ?? (line.details || "Not recorded")}</td>}
                  {showBatch && <td className="tx-preview-detail">{line.batch || "Not recorded"}</td>}
                </tr>)}
              </tbody>
            </table>
          </div>
        </> : <div className="tx-preview-items-empty">No item lines were recorded for this transaction.</div>}
      </section>}

      {history.length > 0 && <div><h3>Saved history</h3>{history.map((entry, index) =>
        <p key={index}>{entry.state} | {entry.timestamp} | {entry.userId || entry.by || ""}</p>)}</div>}
      {kind === "sellreturn" && <p>Refund due is shown separately from stock restoration.</p>}
    </div>
  </div>;
}

export default function PreviewSales(props) {
  return <TransactionRecordPreview {...props} />;
}
